import { useMemo, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router";

import { GaiaMap } from "~/modules/map/components/GaiaMap";
import { GaiaMapUIOverlay } from "~/modules/map/components/GaiaMapUIOverlay";
import { MapButtonFitBounds } from "~/modules/map/components/MapButtonFitBounds";
import { MapBounds } from "~/modules/map/components/MapButtonFitBounds/types";
import { MapButtonToggleBaseMap } from "~/modules/map/components/MapButtonToggleBaseMap";
import { useMapDraw } from "~/modules/map/hooks/useMapDraw";
import { MAP_PANEL_WIDTH_WIDE } from "~/modules/map/mapConstants";
import MapEditorFeatureCreateButton from "~/modules/mapEditor/components/MapEditorFeatureCreateButton";
import EditorMapLayers from "~/modules/mapEditor/components/MapEditorMapView/EditorMapLayers";
import { useMapEditorDrawState } from "~/modules/mapEditor/hooks/useMapEditorDrawState";
import { MapEditorFeatureHoverProvider, useMapEditorFeatureHover } from "~/modules/mapEditor/hooks/useMapEditorFeatureHover";
import { useMapEditorFeatureTypeQuery } from "~/modules/mapEditor/hooks/useMapEditorFeatureTypeQuery";
import { useMapEditorSelection } from "~/modules/mapEditor/hooks/useMapEditorSelection";
import { useMapEditorFilterParams } from "~/modules/project/hooks/useMapEditorFilterParams";
import { useMapFilters } from "~/modules/project/hooks/useMapFilters";
import useRequireCurrentProject from "~/modules/project/hooks/useRequireCurrentProject";
import { isValidBBox } from "~/shared/helpers/isValidBbox";
import { useSessionStorageSearchParams } from "~/shared/hooks/useSessionStorageSearchParams";

import BackToAllVisitsButton from "./components/BackToAllVisitsButton";
import { ProjectReviewVisitV2LeftPanel } from "./components/ProjectReviewVisitV2LeftPanel";
import { VisitInfoCard } from "./components/VisitInfoCard";
import { ProjectReviewVisitV2Screen_GetDataDocument } from "./queries.generated";
import useProjectReviewVisitV2Screen_GetData from "./useProjectReviewVisitV2Screen_GetData";

export function ProjectReviewVisitV2Screen() {
    const { clientSlug, projectSlug, mapEditorFeatureId, visitId } = useParams();
    if (!clientSlug || !projectSlug || !visitId) throw new Error("invalid params");

    const { project } = useRequireCurrentProject();

    return (
        <MapEditorFeatureHoverProvider>
            <ProjectReviewVisitV2ScreenImpl
                projectId={project.id}
                plannerVisitId={visitId}
                mapEditorFeatureId={mapEditorFeatureId}
            />
        </MapEditorFeatureHoverProvider>
    );
}

interface ProjectReviewVisitV2ScreenProps {
    projectId: string;
    plannerVisitId: string;
    mapEditorFeatureId?: string;
}

function ProjectReviewVisitV2ScreenImpl({ projectId, plannerVisitId, mapEditorFeatureId }: ProjectReviewVisitV2ScreenProps) {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSessionStorageSearchParams({ bucket: plannerVisitId });

    const mapParams = useMapEditorFilterParams({ visitIds: [plannerVisitId] });
    const selectedLayerId = searchParams.get("layerId") ?? undefined;
    const { filters, setFilter } = useMapFilters();

    const hovering = useMapEditorFeatureHover();
    const mapEditorFeatureTypeQuery = useMapEditorFeatureTypeQuery();

    const { mapData, layerData, filterOptions, researchTypes, observationTypes, projectData, visit } =
        useProjectReviewVisitV2Screen_GetData(projectId, plannerVisitId, mapParams);

    const handleSelectFeature = async (_: string, selectedMapEditorFeatureId: string) => {
        const response = await mapEditorFeatureTypeQuery(selectedMapEditorFeatureId);

        if (response === "GqlEcologicalFunction") {
            navigate(`functions/created/${selectedMapEditorFeatureId}`);
        }

        if (response === "GqlObservation") {
            navigate(`observations/${selectedMapEditorFeatureId}`);
        }
    };

    // without features, center on the visit area the map initially opened on
    const visitBoundingBox = visit?.plannerArea?.boundingBox;
    const visitBounds = useMemo<MapBounds | undefined>(
        () => (visitBoundingBox && isValidBBox(visitBoundingBox) ? { bbox: visitBoundingBox, kind: "visit" } : undefined),
        [visitBoundingBox],
    );

    const { mapBounds, adjustedMapPadding, selectedFeature } = useMapEditorSelection({
        allLayerData: mapData,
        selectedFeatureId: mapEditorFeatureId,
        selectedLayerId,
        fallbackBounds: visitBounds,
    });

    const drawState = useMapEditorDrawState({
        selectedLayerId,
        selectedFeature,
        projectId,
        visitId: plannerVisitId,
        onSelect: handleSelectFeature,
        featureMutateRefetchQueries: [ProjectReviewVisitV2Screen_GetDataDocument],
    });

    const [map, setMap] = useState<mapboxgl.Map | null>(null);

    useMapDraw({
        map,
        enabled: true,
        selectedFeatureId: mapEditorFeatureId,
        selectedFeature,
        onDeselect: drawState.onDeselect,
        onFeatureCreate: drawState.onFeatureCreate,
        onFeatureUpdate: drawState.onFeatureUpdate,
        selectedDrawMode: drawState.drawMode,
    });

    const handleLayerSelect = (nextMapEditorLayerId: string | undefined) => {
        if (nextMapEditorLayerId === undefined) return;

        setSearchParams(current => {
            current.set("layerId", nextMapEditorLayerId);
            return current;
        });
    };

    if (!visit?.plannerArea?.boundingBox || !isValidBBox(visit.plannerArea.boundingBox)) {
        return null;
    }

    return (
        <GaiaMap
            sx={{ width: "100%", height: "100%" }}
            bounds={visit.plannerArea.boundingBox}
            padding={{ right: MAP_PANEL_WIDTH_WIDE }}
            onMapLoaded={async loadedMap => setMap(loadedMap)}
        >
            <GaiaMapUIOverlay
                leftPanel={
                    <ProjectReviewVisitV2LeftPanel
                        projectId={projectId}
                        layerData={layerData}
                        filterOptions={filterOptions}
                        researchTypes={researchTypes}
                        observationTypes={observationTypes}
                        protocolTreeData={projectData}
                        filters={filters}
                        setFilter={setFilter}
                        givenLeftPanel={
                            <>
                                {/* TODO: it would be nice to extract a <MapPillButton /> */}
                                <BackToAllVisitsButton to="/visits" />
                                <VisitInfoCard visit={visit} />
                            </>
                        }
                        selectedLayerId={selectedLayerId}
                        onLayerSelect={handleLayerSelect}
                    />
                }
                rightPanelWidth="wide"
                rightPanel={<Outlet />}
                topBar={
                    selectedLayerId !== undefined && (
                        <MapEditorFeatureCreateButton
                            selectedOption={drawState.featureCreateOption}
                            setSelectedOption={drawState.setFeatureCreateOption}
                            currentDrawMode={drawState.drawMode}
                            onDrawModeChange={drawState.setDrawMode}
                            allowedDrawTypes={["observation", "ecologicalFunction"]}
                        />
                    )
                }
                bottomBar={
                    <>
                        <MapButtonFitBounds bounds={mapBounds} padding={adjustedMapPadding} />
                        <MapButtonToggleBaseMap />
                    </>
                }
            />
            {mapData?.map(layer => (
                <EditorMapLayers
                    key={layer.id}
                    layer={layer}
                    selectedLayerId={selectedLayerId}
                    selectedFeatureId={mapEditorFeatureId}
                    hoveredFeatureId={hovering.mapEditorFeatureId}
                    onSelect={handleSelectFeature}
                />
            ))}
        </GaiaMap>
    );
}
